// Reader admission is independent of the Loader's current 1.1.0 writer.
export const QUALITY_VERSION_MAX_LENGTH = 32;
const NUMERIC = "(?:0|[1-9][0-9]*)";
const PRERELEASE_ID = "(?:0|[1-9][0-9]*|[0-9]*[A-Za-z-][0-9A-Za-z-]*)";
const SEMVER = new RegExp(
  `^(${NUMERIC})\\.(${NUMERIC})\\.(${NUMERIC})` +
  `(?:-(${PRERELEASE_ID}(?:\\.${PRERELEASE_ID})*))?` +
  "(?:\\+[0-9A-Za-z-]+(?:\\.[0-9A-Za-z-]+)*)?$",
);

export function isSupportedQualityVersion(value: unknown): value is string {
  if (typeof value !== "string" || value.length > QUALITY_VERSION_MAX_LENGTH) return false;
  const match = SEMVER.exec(value);
  // Whole-match comparison rejects a final newline, which JS $ alone permits.
  // Main API/Studio currently admit stable releases in every environment.
  return Boolean(match && match[0] === value && match[1] === "1" && !match[4]);
}

const SENSITIVE_KEYS = new Set([
  "apikey", "authorization", "accesstoken", "refreshtoken", "idtoken", "secret",
  "signature", "signedurl", "token", "xamzsignature", "xgoogsignature",
]);
const SENSITIVE_MARKERS = [
  "bearer ", "?access_token=", "&access_token=", "?token=", "&token=",
  "x-amz-credential=", "x-amz-signature=", "x-goog-credential=", "x-goog-signature=",
];

export function assertSafeWorkerJson(value: unknown): void {
  const ancestors = new Set<object>();
  const visit = (item: unknown): void => {
    if (typeof item === "string") {
      if (SENSITIVE_MARKERS.some((marker) => item.toLowerCase().includes(marker))) {
        throw new Error("loader_validation_fragment_auth_data_forbidden");
      }
    } else if (item !== null && typeof item === "object") {
      if (ancestors.has(item)) throw new Error("loader_validation_fragment_invalid_json");
      ancestors.add(item);
      if (Array.isArray(item)) item.forEach(visit);
      else {
        for (const [key, nested] of Object.entries(item)) {
          if (SENSITIVE_KEYS.has(key.trim().toLowerCase().replace(/[-_]/g, ""))) {
            throw new Error("loader_validation_fragment_auth_data_forbidden");
          }
          visit(nested);
        }
      }
      ancestors.delete(item);
    } else if (item !== null && typeof item !== "boolean" &&
        !(typeof item === "number" && Number.isFinite(item))) {
      throw new Error("loader_validation_fragment_invalid_json");
    }
  };
  visit(value);
}

export function assertWorkerTiming(fragment: Record<string, unknown>): void {
  for (const key of ["workerStartedAtEpochSeconds", "durationSeconds", "queueWaitSeconds"]) {
    if (Object.prototype.hasOwnProperty.call(fragment, key)) {
      const value = fragment[key];
      if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
        throw new Error("loader_validation_fragment_timing_invalid");
      }
    }
  }
  if ("dependsOnStage" in fragment && fragment.dependsOnStage !== "converter") {
    throw new Error("loader_validation_fragment_timing_invalid");
  }
}
